import React, { useState, useEffect, useRef, useMemo } from 'react';
import { fetchGalleryBySlug, fetchGalleriBilleder, parseImageCaptions } from '../lib/cms';
import { imageUrl } from '../lib/images';
import AlbumModal from './AlbumModal';

export default function Gallery({ slug, initialImages, title, pageSize = 12 }) {
  const [gallery, setGallery] = useState(null);
  const [dedicatedBilleder, setDedicatedBilleder] = useState([]);
  const [loading, setLoading] = useState(!initialImages);
  const [selectedAlbumIndex, setSelectedAlbumIndex] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const galleryContainerRef = useRef(null);

  useEffect(() => {
    if (initialImages && initialImages.length > 0) {
      setGallery({ images: initialImages, title: title || 'Galleri' });
      setLoading(false);
      return;
    }

    async function loadGallery() {
      if (!slug) return;
      setLoading(true);
      try {
        const [data, billederFromCollection] = await Promise.all([
          fetchGalleryBySlug(slug),
          fetchGalleriBilleder(slug)
        ]);
        setGallery(data);
        setDedicatedBilleder(billederFromCollection || []);
        setCurrentPage(1);
      } catch (err) {
        console.error('Fejl ved hentning af galleri:', err);
      } finally {
        setLoading(false);
      }
    }
    loadGallery();
  }, [slug, initialImages, title]);

  // Kombiner eller prioriter dedikerede CMS-cases/albummer
  const fallbackCaptions = useMemo(() => {
    return parseImageCaptions(gallery?.billedtekster, gallery?.images?.length || 0);
  }, [gallery?.billedtekster, gallery?.images]);

  const items = useMemo(() => {
    // 1. Hvis der er oprettet dedikerede galleri-cases/albummer i CMS
    if (dedicatedBilleder && dedicatedBilleder.length > 0) {
      return dedicatedBilleder.map((b, idx) => {
        const imgs = (b.images && b.images.length > 0)
          ? b.images
          : (b.url ? [b.url] : []);

        return {
          id: b.id || `album-${idx}`,
          title: b.titel || gallery?.title || 'Projekt',
          description: b.beskrivelse || '',
          url: imgs[0] || b.url || '',
          images: imgs,
          imageCount: imgs.length,
          isAlbum: imgs.length > 1,
          category: b.kategori || gallery?.title || '',
          sortering: b.sortering || idx
        };
      });
    }

    // 2. Fallback til galleriets overordnede billed-array
    const baseImages = gallery?.images || [];
    return baseImages.map((url, idx) => {
      const cap = fallbackCaptions[idx];
      return {
        id: `img-${idx}`,
        title: cap?.title || gallery?.title || 'Galleri',
        description: cap?.description || '',
        url,
        images: [url],
        imageCount: 1,
        isAlbum: false,
        category: gallery?.title || '',
        sortering: idx
      };
    });
  }, [dedicatedBilleder, gallery, fallbackCaptions]);

  const totalAlbums = items.length;
  const totalPages = Math.ceil(totalAlbums / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const currentItems = items.slice(startIndex, startIndex + pageSize);

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-[#325d52] border-t-transparent" />
      </div>
    );
  }

  if (items.length === 0) {
    return null;
  }

  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > totalPages) return;
    setCurrentPage(newPage);
    if (galleryContainerRef.current) {
      const rect = galleryContainerRef.current.getBoundingClientRect();
      if (rect.top < 0) {
        galleryContainerRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  };

  const selectedAlbum = selectedAlbumIndex !== null ? items[selectedAlbumIndex] : null;

  return (
    <div ref={galleryContainerRef} className="space-y-8">
      {/* 1. OVERSIGT OVER ALBUMMER & CASES */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
        {currentItems.map((item, idx) => {
          const globalIndex = startIndex + idx;
          const isMulti = item.isAlbum || item.imageCount > 1;

          return (
            <div
              key={item.id || globalIndex}
              onClick={() => setSelectedAlbumIndex(globalIndex)}
              className="group relative flex flex-col justify-between overflow-hidden rounded-2xl sm:rounded-3xl bg-white border border-gray-200/90 shadow-xs hover:shadow-xl transition-all duration-300 hover:-translate-y-1 hover:border-[#325d52]/50 cursor-pointer"
            >
              {/* Stacked effekt hvis der er flere billeder i albummet */}
              {isMulti && (
                <div
                  className="absolute inset-x-3 -top-1.5 h-3 rounded-t-2xl bg-gray-300/70 border border-gray-300/80 -z-10 group-hover:-top-2 transition-all duration-300"
                  aria-hidden="true"
                />
              )}

              {/* Cover billede */}
              <div className="relative aspect-[4/3] w-full overflow-hidden bg-gray-100">
                <img
                  src={imageUrl(item.url, 'width=700,height=525,fit=cover,quality=85,format=auto')}
                  alt={`${item.title} - Forsidebillede`}
                  className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                  loading="lazy"
                />

                {/* Subtilt gradient overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent opacity-80 group-hover:opacity-60 transition-opacity" />

                {/* Badge: Antal billeder */}
                <div className="absolute top-3 right-3 z-10">
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-black/75 px-3 py-1 text-xs font-semibold text-white backdrop-blur-md border border-white/20 shadow-md group-hover:bg-[#325d52] group-hover:border-[#deecd4]/40 transition-colors">
                    <i className={isMulti ? 'fa-solid fa-layer-group text-[#deecd4]' : 'fa-solid fa-camera text-[#deecd4]'} />
                    <span>
                      {item.imageCount} {item.imageCount === 1 ? 'billede' : 'billeder'}
                    </span>
                  </span>
                </div>

                {/* Hurtig forstørrelsesindikator ved hover */}
                <div className="absolute bottom-3 right-3 z-10 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-white/90 text-[#1b453f] shadow-lg backdrop-blur-xs">
                    <i className="fa-solid fa-arrow-up-right-from-square text-xs" />
                  </span>
                </div>
              </div>

              {/* Tekst og albumoplysninger */}
              <div className="flex flex-1 flex-col justify-between p-4 sm:p-5">
                <div>
                  <h3 className="text-base sm:text-lg font-bold text-gray-900 group-hover:text-[#325d52] transition-colors line-clamp-1">
                    {item.title}
                  </h3>
                  {item.description ? (
                    <p className="mt-1 text-xs sm:text-sm text-gray-600 line-clamp-2 leading-relaxed">
                      {item.description}
                    </p>
                  ) : (
                    <p className="mt-1 text-xs text-gray-400 italic">
                      Se billeder og udført arbejde
                    </p>
                  )}
                </div>

                {/* Handlingsknap i bunden */}
                <div className="mt-4 flex items-center justify-between border-t border-gray-100 pt-3 text-xs font-semibold text-[#325d52]">
                  <span className="inline-flex items-center gap-1.5">
                    <i className="fa-solid fa-folder-open text-[#245b53]" />
                    <span>{isMulti ? `Åbn album (${item.imageCount} fotos)` : 'Se stort billede'}</span>
                  </span>
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-[#deecd4] text-[#1b453f] transition-transform group-hover:translate-x-1">
                    <i className="fa-solid fa-chevron-right text-[10px]" />
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* 2. PAGINERING (HVIS DER ER FLERE SIDER MED ALBUMMER) */}
      {totalPages > 1 && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-6 border-t border-gray-200">
          <p className="text-xs sm:text-sm text-gray-600 order-2 sm:order-1">
            Viser <span className="font-semibold text-gray-900">{startIndex + 1}</span>–
            <span className="font-semibold text-gray-900">{Math.min(startIndex + pageSize, totalAlbums)}</span> af{' '}
            <span className="font-semibold text-gray-900">{totalAlbums}</span> projekter
          </p>

          <div className="flex items-center gap-1.5 order-1 sm:order-2">
            {/* Forrige knap */}
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs sm:text-sm font-medium transition-all ${
                currentPage === 1
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 hover:text-[#325d52] hover:border-gray-300 shadow-2xs cursor-pointer'
              }`}
              aria-label="Forrige side"
            >
              <i className="fa-solid fa-chevron-left text-xs" />
              <span>Forrige</span>
            </button>

            {/* Sidenumre */}
            <div className="flex items-center gap-1">
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNum) => {
                const isActive = pageNum === currentPage;
                return (
                  <button
                    key={pageNum}
                    onClick={() => handlePageChange(pageNum)}
                    className={`h-9 w-9 rounded-xl text-xs sm:text-sm font-semibold transition-all cursor-pointer ${
                      isActive
                        ? 'bg-[#325d52] text-white shadow-xs'
                        : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 hover:text-[#325d52] hover:border-gray-300 shadow-2xs'
                    }`}
                    aria-label={`Side ${pageNum}`}
                    aria-current={isActive ? 'page' : undefined}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>

            {/* Næste knap */}
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs sm:text-sm font-medium transition-all ${
                currentPage === totalPages
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 hover:text-[#325d52] hover:border-gray-300 shadow-2xs cursor-pointer'
              }`}
              aria-label="Næste side"
            >
              <span>Næste</span>
              <i className="fa-solid fa-chevron-right text-xs" />
            </button>
          </div>
        </div>
      )}

      {/* 3. ALBUM VIEWER MODAL */}
      <AlbumModal
        isOpen={selectedAlbumIndex !== null}
        album={selectedAlbum}
        onClose={() => setSelectedAlbumIndex(null)}
        onPrevAlbum={
          selectedAlbumIndex !== null && selectedAlbumIndex > 0
            ? () => setSelectedAlbumIndex(selectedAlbumIndex - 1)
            : null
        }
        onNextAlbum={
          selectedAlbumIndex !== null && selectedAlbumIndex < items.length - 1
            ? () => setSelectedAlbumIndex(selectedAlbumIndex + 1)
            : null
        }
        prevAlbumTitle={
          selectedAlbumIndex !== null && selectedAlbumIndex > 0
            ? items[selectedAlbumIndex - 1]?.title
            : ''
        }
        nextAlbumTitle={
          selectedAlbumIndex !== null && selectedAlbumIndex < items.length - 1
            ? items[selectedAlbumIndex + 1]?.title
            : ''
        }
      />
    </div>
  );
}
